import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { confirmDialog } from "../ui/confirm-dialog";

const InventoryAccessories = ({ axiosInstance, selectedWarehouse, warehouses = [] }) => {
  const [accessories, setAccessories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandFilter, setBrandFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [sortOption, setSortOption] = useState("alphabetical");

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupAccessories, setGroupAccessories] = useState([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedAccessory, setSelectedAccessory] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.email === 'vinsimon23@gmail.com';

  useEffect(() => {
    if (!axiosInstance) return;
    fetchAccessories();
    fetchBrands();
  }, [axiosInstance]);

  const fetchAccessories = async () => {
    try {
      const response = await axiosInstance.get('/stock/accessories');
      setAccessories(response.data || []);
    } catch (error) {
      console.error("Error fetching accessories:", error);
      toast.error("Failed to fetch accessories");
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axiosInstance.get('/stock/accessories/brands');
      setBrands(response.data || []);
    } catch (error) {
      console.error("Error fetching accessory brands:", error);
      setBrands([]);
    }
  };

  const getWarehouseName = (id) => {
    if (!id) return "Unassigned";
    const w = warehouses.find(x => x.id === id);
    return w ? w.name : "Unknown";
  };

  const filteredAccessories = useMemo(() => {
    const searchLower = (searchFilter || '').toLowerCase();
    return accessories.filter(a => {
      const matchesBrand = brandFilter && brandFilter !== 'all' ? a.brand === brandFilter : true;
      const matchesSearch = !searchFilter ||
        (a.model?.toLowerCase().includes(searchLower) || a.itemDescription?.toLowerCase().includes(searchLower));
      const matchesWarehouse = !selectedWarehouse || a.warehouse_id === selectedWarehouse.id;
      return matchesBrand && matchesSearch && matchesWarehouse;
    });
  }, [accessories, brandFilter, searchFilter, selectedWarehouse]);

  const aggregatedAccessories = useMemo(() => {
    const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const map = filteredAccessories.reduce((acc, a) => {
      const key = `${normalize(a.brand)}-${normalize(a.model)}-${normalize(a.itemDescription)}-${a.warehouse_id || 'null'}`;
      if (!acc[key]) {
        acc[key] = { ...a, totalQuantity: 0, count: 0 };
      }
      acc[key].totalQuantity += (a.quantity || 0);
      acc[key].count += 1;
      return acc;
    }, {});
    return Object.values(map);
  }, [filteredAccessories]);

  const sortedAggregatedAccessories = useMemo(() => {
    return [...aggregatedAccessories].sort((a, b) => {
      switch (sortOption) {
        case 'largest_qty':
          return (b.totalQuantity || 0) - (a.totalQuantity || 0);
        case 'lowest_qty':
          return (a.totalQuantity || 0) - (b.totalQuantity || 0);
        case 'alphabetical':
        default:
          return (a.brand || '').localeCompare(b.brand || '') || (a.model || '').localeCompare(b.model || '');
      }
    });
  }, [aggregatedAccessories, sortOption]);

  const openDetails = (groupRow) => {
    if (!isSuperAdmin) return;
    const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const group = filteredAccessories.filter(a =>
      normalize(a.brand) === normalize(groupRow.brand) &&
      normalize(a.model) === normalize(groupRow.model) &&
      normalize(a.itemDescription) === normalize(groupRow.itemDescription) &&
      (a.warehouse_id || 'null') === (groupRow.warehouse_id || 'null')
    );
    setSelectedGroup(groupRow);
    setGroupAccessories(group);
    setIsDetailsOpen(true);
  };

  const handleEditAccessory = (accessory) => {
    setSelectedAccessory({ ...accessory });
    setIsEditOpen(true);
  };

  const handleDeleteAccessory = async (id) => {
    const confirmed = await confirmDialog({
      title: "Delete Accessory",
      description: "Are you sure you want to delete this accessory entry?",
      variant: "destructive"
    });
    if (!confirmed) return;

    try {
      await axiosInstance.delete(`/stock/accessories/${id}`);
      toast.success("Accessory deleted successfully");
      await fetchAccessories();
      setGroupAccessories(prev => prev.filter(x => x.id !== id));
    } catch (error) {
      console.error("Error deleting accessory:", error);
      toast.error("Failed to delete accessory");
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedAccessory(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateAccessory = async () => {
    if (!selectedAccessory) return;
    try {
      const payload = {
        warehouse_id: selectedAccessory.warehouse_id || null,
        brand: selectedAccessory.brand,
        model: selectedAccessory.model,
        itemDescription: selectedAccessory.itemDescription,
        quantity: parseInt(selectedAccessory.quantity) || 0
      };
      await axiosInstance.put(`/stock/accessories/${selectedAccessory.id}`, payload);
      toast.success("Accessory updated successfully");
      setIsEditOpen(false);
      setSelectedAccessory(null);
      await fetchAccessories();
    } catch (error) {
      console.error("Error updating accessory:", error);
      toast.error("Failed to update accessory");
    }
  };

  return (
    <div className="h-full flex flex-col space-y-4">
      <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-end">
        <div className="w-[200px] space-y-2">
          <Label>Brand</Label>
          <Select value={brandFilter} onValueChange={setBrandFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Select Brand" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Brands</SelectItem>
              {brands.map(brand => (
                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 space-y-2 min-w-[200px]">
          <Label>Search</Label>
          <Input
            placeholder="Search Model or Description..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
        </div>

        <div className="w-[220px] space-y-2">
          <Label>Sort</Label>
          <Select value={sortOption} onValueChange={setSortOption}>
            <SelectTrigger>
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
              <SelectItem value="largest_qty">Largest Quantity</SelectItem>
              <SelectItem value="lowest_qty">Lowest Quantity</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden flex-1">
        <div className="border rounded-md m-4 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Warehouse</TableHead>
                <TableHead className="font-bold">Brand</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Total Quantity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedAggregatedAccessories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                    No accessories found.
                  </TableCell>
                </TableRow>
              ) : (
                sortedAggregatedAccessories.map((row, idx) => (
                  <TableRow
                    key={`${row.warehouse_id || 'null'}-${row.brand}-${row.model}-${idx}`}
                    className={isSuperAdmin ? "hover:bg-orange-50 cursor-pointer transition-colors" : ""}
                    onClick={() => openDetails(row)}
                  >
                    <TableCell>{getWarehouseName(row.warehouse_id)}</TableCell>
                    <TableCell className="font-bold">{row.brand}</TableCell>
                    <TableCell>{row.model}</TableCell>
                    <TableCell>{row.itemDescription}</TableCell>
                    <TableCell>{row.totalQuantity}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-[85vw] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {selectedGroup ? `${selectedGroup.brand} ${selectedGroup.model}` : 'Accessory Details'}
            </DialogTitle>
            <DialogDescription>
              Warehouse: {selectedGroup ? getWarehouseName(selectedGroup.warehouse_id) : '-'} • Entries: {groupAccessories.length}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  {isSuperAdmin && <TableHead>Added By</TableHead>}
                  {isSuperAdmin && <TableHead>Date Created</TableHead>}
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupAccessories.map(a => (
                  <TableRow key={a.id}>
                    {isSuperAdmin && <TableCell>{a.addedBy || '-'}</TableCell>}
                    {isSuperAdmin && <TableCell>{a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' }) : '-'}</TableCell>}
                    <TableCell>{getWarehouseName(a.warehouse_id)}</TableCell>
                    <TableCell>{a.brand}</TableCell>
                    <TableCell>{a.model}</TableCell>
                    <TableCell>{a.itemDescription}</TableCell>
                    <TableCell>{a.quantity}</TableCell>
                    <TableCell className="text-right">
                      {isSuperAdmin ? (
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="icon" onClick={() => handleEditAccessory(a)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="icon" onClick={() => handleDeleteAccessory(a.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Accessory</DialogTitle>
            <DialogDescription>Update this accessory entry.</DialogDescription>
          </DialogHeader>

          {selectedAccessory && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Warehouse</Label>
                <Select
                  value={selectedAccessory.warehouse_id || ''}
                  onValueChange={(value) => setSelectedAccessory(prev => ({ ...prev, warehouse_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Brand</Label>
                <Input name="brand" value={selectedAccessory.brand || ''} onChange={handleEditInputChange} />
              </div>
              <div className="grid gap-2">
                <Label>Model</Label>
                <Input name="model" value={selectedAccessory.model || ''} onChange={handleEditInputChange} />
              </div>
              <div className="grid gap-2">
                <Label>Description</Label>
                <Input name="itemDescription" value={selectedAccessory.itemDescription || ''} onChange={handleEditInputChange} />
              </div>
              <div className="grid gap-2">
                <Label>Quantity</Label>
                <Input name="quantity" type="number" value={selectedAccessory.quantity || 0} onChange={handleEditInputChange} />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateAccessory}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryAccessories;

